import {
  QueryKey,
  useMutation,
  useQueryClient,
  UseMutationResult,
} from 'react-query';
import { useIntl } from 'react-intl';
import {
  showErrorNotification,
  showSuccessNotification,
} from './assets/useAssets';
import { errorMessageId } from './errors';

export interface NotifiedMutationOptions<TData, TVariables> {
  /** Message id shown via a success notification when the mutation succeeds. */
  successId?: string;
  /**
   * Message id shown via an error notification when the mutation fails. Used as
   * a fallback: well-known HTTP statuses (403/404/5xx) show a more specific
   * message instead (see `errorMessageId`).
   */
  errorId?: string;
  /** Query key to invalidate on success (a single key, e.g. `['customers']`). */
  invalidate?: QueryKey;
  /** Extra side-effect after the success notification/invalidation (e.g. navigation). */
  onSuccess?: (data: TData, variables: TVariables) => void;
  /** Extra side-effect after the error notification. */
  onError?: (error: unknown, variables: TVariables) => void;
}

/**
 * Wraps `useMutation` with the boilerplate repeated across the cockpit hooks:
 * a success/error notification (by message id) and a query invalidation on
 * success. Pass `onSuccess`/`onError` for anything extra (navigation, etc.).
 */
export function useNotifiedMutation<TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: NotifiedMutationOptions<TData, TVariables> = {},
): UseMutationResult<TData, unknown, TVariables> {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation<TData, unknown, TVariables>(mutationFn, {
    onSuccess: (data, variables) => {
      if (options.successId) {
        showSuccessNotification(options.successId, intl);
      }
      if (options.invalidate !== undefined) {
        queryClient.invalidateQueries(options.invalidate);
      }
      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (options.errorId) {
        // Upgrade the message for well-known HTTP statuses (forbidden, not
        // found, server error); otherwise fall back to the caller's errorId.
        showErrorNotification(errorMessageId(error, options.errorId), intl);
      }
      options.onError?.(error, variables);
    },
  });
}
